import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Components 
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import DataBox from '../../../general/data/DataBox';
import InfoRow from '../../../general/inputs/InfoRow';
import WarningIcon from '@mui/icons-material/Warning';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import getCapitalizedString from '../../../../util/style/getCapitalizedString';
import Button from '../../../general/inputs/Button';
import { setDashboardSection } from '../../../../redux/features/general/navigation';

const PaymentMethod: React.FC = () => {
    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const colors = useAppSelector(state => state.theme.colors); 
    const paymentMethod = useAppSelector(state => state.billing.paymentMethod); 
    const customer = useAppSelector(state => state.billing.customer); 
    const subscription = useAppSelector(state => state.billing.subscription); 
    
    // --- START OF FIX ---
    // Get the group object from Redux to check for special client status.
    const group = useAppSelector(state => state.billing.group);

    // A special client is one with a custom trial end date or a custom price ID.
    const isSpecialClient = group?.trialEndDate || group?.customPriceId;

    // If this is a special client, do not render this component at all.
    if (isSpecialClient) {
        return null;
    }
    // --- END OF FIX ---

    const handlePaywallNavigation = () => { 
        dispatch(setDashboardSection('plans')); 
    }; 

    const renderExistingPaymentMethod = () => (
        <DataBox title={String(t('paymentMethod'))}>
            <InfoRow 
                icon={<CreditCardIcon sx={{ color: colors?.main }} />}
                title='Card'
                description={`**** ${paymentMethod?.card?.last4}`}
                descriptionStyle={{ color: 'rgb(102, 101, 99)', fontWeight: 'bold' }}
                isDisabled
            />
            <InfoRow 
                icon={<AccountBalanceIcon sx={{ color: colors?.main }} />}
                title='Type'
                description={getCapitalizedString(paymentMethod?.card?.brand)}
                descriptionStyle={{ color: 'rgb(99, 67, 9)'}}
                isDisabled
            />
            <InfoRow 
                icon={<AccountBoxOutlinedIcon sx={{ color: colors?.main }} />}
                title='cardHolder'
                description={customer?.name?.toUpperCase()}
                isDisabled
            />
            <InfoRow 
                icon={<MailOutlineIcon sx={{ color: colors?.main }} />}
                title='Email'
                description={paymentMethod?.billing_details?.email}
                isDisabled
            />
        </DataBox>
    );

    const renderAddPaymentMethodWarning = () => (
        <DataBox 
            title={String(t('paymentMethod'))} 
            extraStyle={{
                backgroundColor: 'rgb(220, 220, 220)',
                border: '1px solid rgb(194, 127, 11)',
                paddingTop: 0 
            }}
        >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                p: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: 'rgb(194, 127, 11)' }} />
                    <Typography variant="body2" sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                        {String(t('addPaymentMethodToKeepSubscription'))}
                    </Typography>
                </Box>
                <Button 
                    icon={<AccountBalanceWalletOutlinedIcon sx={{ color: 'white', mr: 1, fontSize: 18 }} />}
                    title={String(t('addPaymentMethod'))}
                    triggerFunction={handlePaywallNavigation}
                    extraStyle={{ 
                        backgroundColor: 'rgb(194, 127, 11)',
                        '&:hover': { backgroundColor: 'rgb(174, 107, 11)'},
                        flexShrink: 0,
                        width: isMobile ? '100%' : 'auto',
                        height: 'auto',
                        minHeight: '40px',
                        whiteSpace: 'normal',
                        lineHeight: '1.3',
                        padding: '6px 12px',
                        textAlign: 'center',
                    }}
                />
            </Box>
        </DataBox>
    );

    return (
        <Box sx={{ width: '100%', maxWidth: '600px' }}>
            {paymentMethod && subscription?.status !== 'canceled' 
                ? renderExistingPaymentMethod()
                : null
            }
            {!paymentMethod && subscription?.status !== 'canceled'
                ? renderAddPaymentMethodWarning()
                : null
            }
        </Box>
    );
};

export default PaymentMethod;